/**
 * SortableHeader — the one way a table column header sorts (TF3 P41/P44, Pedro 2026-10-15:
 * "all lists need to be able to be both filtered as well as sorted by all column headers").
 *
 * Click a header to sort by it (A→Z / smallest first); click again to reverse; one column
 * at a time. The arrow shows the active column and direction; the others show a faint ⇅.
 * Works with the per-column filters below it (ColumnFilters, C-132): the header row sorts,
 * the second row filters.
 *
 * Client-side tables use `sortRows`; server-paged lists pass the same {key, dir} to the
 * endpoint instead (P41).
 */
import { Table, Text, Tooltip, UnstyledButton } from '@mantine/core';
import { IconChevronDown, IconChevronUp, IconSelector } from '@tabler/icons-react';

import { FilteredColumnLabel } from './ColumnFilters';

export type SortDir = 'asc' | 'desc';
export interface SortState<K extends string> { key: K; dir: SortDir }

/** Next sort state after clicking `key`: same column → reverse; new column → ascending. */
export function nextSort<K extends string>(s: SortState<K> | null, key: K): SortState<K> {
  return s && s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' };
}

/** The server's sort parameter: "key" or "-key"; undefined = the list's default order. */
export function sortParam<K extends string>(s: SortState<K> | null): string | undefined {
  return s ? (s.dir === 'desc' ? `-${s.key}` : s.key) : undefined;
}

// F6 (revised by Pedro 2026-10-15 after the P41 smoke): a list's sort lives in the page's
// own state and clears when you navigate away. Nothing is remembered across pages.

/** Stable client-side sort. `value` returns the comparable for a column; null / undefined
 * always sort last, whatever the direction. Strings compare accent- and case-insensitively. */
export function sortRows<T, K extends string>(
  rows: T[], s: SortState<K> | null, value: (row: T, key: K) => string | number | null | undefined,
): T[] {
  if (!s) return rows;
  const mul = s.dir === 'asc' ? 1 : -1;
  return rows
    .map((row, i) => ({ row, i, v: value(row, s.key) }))
    .sort((a, b) => {
      const an = a.v == null || a.v === '';
      const bn = b.v == null || b.v === '';
      if (an || bn) return an === bn ? a.i - b.i : an ? 1 : -1;
      const c = typeof a.v === 'number' && typeof b.v === 'number'
        ? a.v - b.v
        : String(a.v).localeCompare(String(b.v), undefined, { sensitivity: 'base', numeric: true });
      return c * mul || a.i - b.i;
    })
    .map((x) => x.row);
}

export function SortableTh<K extends string>({
  label, col, sort, onSort, filtered = false, w, ta, tooltip,
}: {
  label: string; col: K; sort: SortState<K> | null; onSort: (k: K) => void;
  filtered?: boolean; w?: number | string; ta?: 'left' | 'right';
  /** Optional explanation of the column, shown on hover. */
  tooltip?: string;
}) {
  const active = sort?.key === col;
  const Icon = !active ? IconSelector : sort?.dir === 'asc' ? IconChevronUp : IconChevronDown;
  const button = (
    <UnstyledButton onClick={() => onSort(col)} aria-label={`Sort by ${label}`}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <FilteredColumnLabel active={filtered}><Text size="sm" fw={600}>{label}</Text></FilteredColumnLabel>
      <Icon size={14} style={{ opacity: active ? 1 : 0.4 }} />
    </UnstyledButton>
  );
  return (
    <Table.Th w={w} ta={ta}>
      {tooltip ? <Tooltip label={tooltip} withArrow>{button}</Tooltip> : button}
    </Table.Th>
  );
}
